import { useEffect, useRef, useState } from 'react';
import type { ImageFilter, ImageMask, ViewId, DesignDocument } from '@ox/schema';
import type { DesignerProps } from './types';
import { useEditorStore } from './store/editor-store';
import { useIsMobile } from './hooks/useIsMobile';
import { useLiveQuote } from './hooks/useLiveQuote';
import { DesignerCanvas } from './components/Canvas';
import { LeftToolbar } from './components/LeftToolbar';
import { HomePanel } from './components/HomePanel';
import { GalleryPanel } from './components/GalleryPanel';
import { AddTextPanel } from './components/AddTextPanel';
import { TextPanel } from './components/TextPanel';
import { ImagePanel } from './components/ImagePanel';
import { EditImageModal, type PendingImageDraft } from './components/EditImageModal';
import { ViewSwitcher } from './components/ViewSwitcher';
import { FooterBar } from './components/FooterBar';
import { DesktopTopBar } from './components/DesktopTopBar';
import { ChangeProductModal } from './components/ChangeProductModal';
import { SizeQtyModal } from './components/SizeQtyModal';
import { AddedToCartModal } from './components/AddedToCartModal';
import { PreviewModal } from './components/PreviewModal';
import { PriceBreakdownSheet } from './components/PriceBreakdownSheet';
import { getUsedViewMetrics } from './lib/viewMetrics';
import { printServiceQuoteFields, modelQuoteFields } from './lib/printServiceQuote';
import { uiThemeStyle } from './lib/uiTheme';
import {
  MobileHeader,
  MobileCanvasOverlays,
  MobileProductActions,
  MobileTabBar,
  MobileViewSwitcher,
} from './components/mobile/MobileChrome';
import { TextAddSheet, TextEditSheet, GarmentColorSheet } from './components/mobile/TextSheets';
import { ImageEditSheet } from './components/mobile/ImageSheets';
import { UploadSheet, EditImageSheet, rememberUploadedAsset } from './components/mobile/UploadSheets';
import { GallerySheet } from './components/mobile/GallerySheet';
import {
  ProductDetailsSheet,
  ChangeProductSheet,
  PreviewSheet,
} from './components/mobile/ProductSheets';
import { fetchAssetMeta, uploadImageFile } from './lib/uploadAsset';
import { loadRecentAssets } from './lib/recentAssets';
import { maskViewportSizeMm } from './lib/maskFit';
import { fitImageToPrintArea, probeImagePixelSize } from './lib/imageFit';
import { preloadImageUrl } from './components/MaskFitPreview';
import { CanvasPrivacyBanner } from './components/CanvasPrivacyBanner';
import { SaveAsTemplateModal } from './components/SaveAsTemplateModal';
import { buildTemplateVariantsFromModel } from './lib/templateVariants';
import { claimGuestOwnership, oxAuthHeaders, syncGuestId } from './lib/guestId';
import { chooseSizeCtaLabel, colorKeyWithVariants, isFreeHexColorKey } from './lib/modelAxes';
import './styles.css';

type MobileSheet =
  | null
  | 'addText'
  | 'text'
  | 'image'
  | 'upload'
  | 'gallery'
  | 'editImage'
  | 'garmentColor'
  | 'productDetails'
  | 'changeProduct'
  | 'preview';

export function Designer(props: DesignerProps) {
  const apiBase = props.apiBase ?? 'http://localhost:4010';
  const isMobile = useIsMobile(768);
  const setModel = useEditorStore((s) => s.setModel);
  const replaceDocument = useEditorStore((s) => s.replaceDocument);
  const remasureAllText = useEditorStore((s) => s.remasureAllText);
  const document = useEditorStore((s) => s.document);
  const panel = useEditorStore((s) => s.panel);
  const setPanel = useEditorStore((s) => s.setPanel);
  const addImage = useEditorStore((s) => s.addImage);
  const addGalleryImage = useEditorStore((s) => s.addGalleryImage);
  const updateObject = useEditorStore((s) => s.updateObject);
  const setLowResWarning = useEditorStore((s) => s.setLowResWarning);
  const model = useEditorStore((s) => s.model);
  const selectedId = useEditorStore((s) => s.selectedId);
  const select = useEditorStore((s) => s.select);
  const activeView = useEditorStore((s) => s.activeView);
  const setActiveView = useEditorStore((s) => s.setActiveView);

  const initialDesignId = props.designId?.trim() && props.designId.trim() !== 'draft'
    ? props.designId.trim()
    : 'draft';
  const [designId, setDesignId] = useState(initialDesignId);
  const [versionId, setVersionId] = useState(initialDesignId === 'draft' ? 'draft' : initialDesignId);
  const designIdRef = useRef(designId);
  designIdRef.current = designId;
  const [saving, setSaving] = useState(false);
  const [showSizes, setShowSizes] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mobileSheet, setMobileSheet] = useState<MobileSheet>(null);
  const [productsMode, setProductsMode] = useState(false);
  const [added, setAdded] = useState<{
    articleCount: number;
    redirectUrl?: string;
  } | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [cartUrl, setCartUrl] = useState<string | undefined>();
  const [designPreviewUrls, setDesignPreviewUrls] = useState<Partial<Record<ViewId, string>>>({});
  const [pendingImage, setPendingImage] = useState<PendingImageDraft | null>(null);
  /** True while uploadFile is still awaiting assetId (edit sheet already open with local preview). */
  const [imageUploading, setImageUploading] = useState(false);
  /** When re-editing an object already on the canvas */
  const [reeditObjectId, setReeditObjectId] = useState<string | null>(null);
  const [loadingDesign, setLoadingDesign] = useState(initialDesignId !== 'draft');
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [liveBreakdownOpen, setLiveBreakdownOpen] = useState(false);
  const [changeProductOpen, setChangeProductOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplBusy, setSaveTplBusy] = useState(false);
  const [saveTplError, setSaveTplError] = useState('');
  const [tplToast, setTplToast] = useState<{ title: string; url?: string } | null>(null);
  const [tplCollections, setTplCollections] = useState<Array<{ id: string; name: string; slug?: string }>>(
    [],
  );
  const [tplCollectionsLoading, setTplCollectionsLoading] = useState(false);
  const galleryBootstrapped = useRef(false);

  function applyPrintOptsFromUrl() {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const rawType = (params.get('ox_print_type') || params.get('print_type') || '').trim();
    const store = useEditorStore.getState();
    if (rawType === 'white_base' || rawType === 'no_white_base') {
      store.setGarmentPrintType(rawType);
    } else if (!store.document.selection.garmentPrintType && store.model?.printService) {
      // leave unset so UI prompts
    }
    const franela = params.get('ox_franela') || params.get('franela');
    if (franela === '1' || franela === 'true' || franela === 'yes') {
      store.setFranelaReactiva(true);
    }
  }

  const liveQuote = useLiveQuote(props.ports.pricing, designId);
  /** Default true — Studio / design-only embeds pass commerce={false}. */
  const commerce = props.commerce !== false;
  /** Show only for hosts that inject Studio createFromDesign (active members). */
  const canSaveTemplate = commerce && !!props.ports.studio?.createFromDesign;
  const priceLabel = liveQuote.label;
  const liveColorLabel =
    model?.colors.find((c) => c.key === liveQuote.colorKey)?.label ?? liveQuote.colorKey;

  const cartKeyFromUrl =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('ox_cart_key') || ''
      : '';
  const editingExisting = designId !== 'draft' || initialDesignId !== 'draft';
  const fromCart = cartKeyFromUrl !== '';
  const saveLabel = editingExisting ? 'Actualizar' : 'Guardar';
  const chooseSizeLabel = chooseSizeCtaLabel(model, fromCart);
  const mobileSaveLabel = editingExisting ? '💾 Actualizar' : '💾 Guardar';

  // Guest → logged-in: transfer designs/assets owned by the pre-login guest id.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await props.ports.identity.getToken();
        const ownerRef = await props.ports.identity.getOwnerRef();
        if (!token || !ownerRef || ownerRef.startsWith('guest:') || cancelled) return;
        let cookieGuest = '';
        try {
          const m = globalThis.document?.cookie?.match(/(?:^|;\s*)ox_guest=([^;]+)/);
          const raw = m?.[1];
          cookieGuest = raw ? decodeURIComponent(raw) : '';
        } catch {
          /* ignore */
        }
        const localGuest = (() => {
          try {
            return localStorage.getItem('ox_guest_id') || '';
          } catch {
            return '';
          }
        })();
        const ids = [...new Set([cookieGuest, localGuest].filter(Boolean))];
        for (const guestId of ids) {
          if (cancelled) return;
          syncGuestId(guestId);
          await claimGuestOwnership(apiBase, { token, ownerRef, guestId });
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, props.ports.identity]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loadId = props.designId?.trim();
        const action =
          props.designAction?.trim() ||
          (typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('ox_action') || ''
            : '');
        let loadedDoc: DesignDocument | null = null;
        let loadedDesignMeta: { id: string; versionId: string } | null = null;

        if (loadId && loadId !== 'draft') {
          setLoadingDesign(true);
          try {
            const token = await props.ports.identity.getToken();
            const res = await fetch(`${apiBase.replace(/\/$/, '')}/designs/${encodeURIComponent(loadId)}`, {
              headers: {
                Accept: 'application/json',
                ...oxAuthHeaders(token),
              },
            });
            if (res.ok) {
              const data = (await res.json()) as {
                id?: string;
                versionId?: string;
                templateId?: string;
                document?: DesignDocument;
              };
              if (cancelled) return;
              if (data.document) {
                loadedDoc = data.document;
                loadedDesignMeta = {
                  id: data.id || loadId,
                  versionId: data.versionId || data.id || loadId,
                };
              }
            }
          } catch {
            // Fall through — still try empty model
          }
        }

        const modelRef =
          (props.modelId?.trim() || '') ||
          loadedDoc?.templateId ||
          '';
        if (!modelRef) {
          if (!cancelled) setLoadingDesign(false);
          return;
        }

        const m = await props.ports.catalog.getModel(modelRef);
        if (cancelled) return;

        if (loadedDoc && loadedDesignMeta) {
          // URL/PDP/cart garment color wins over a catalog remap left on the saved design.
          const colorFromDoc = loadedDoc.selection?.colorKey;
          const preferredColor = (props.colorKey?.trim() || colorFromDoc || '').trim();
          setModel(m, preferredColor || props.colorKey);
          replaceDocument(loadedDoc);
          // Remap only when the saved color is unknown / not a free garment hex —
          // never overwrite a custom PDP garment color with ox_color_hex from catalog.
          const live = useEditorStore.getState();
          if (live.model) {
            const current = preferredColor || live.document.selection.colorKey;
            const known =
              isFreeHexColorKey(current) ||
              live.model.colors.some((c) => c.key === current) ||
              live.model.variants.some((v) => v.colorKey === current);
            if (preferredColor && (isFreeHexColorKey(preferredColor) || props.colorKey?.trim())) {
              // Re-apply URL/cart garment hex after replaceDocument wiped synthetic swatch.
              live.setColor(preferredColor);
            } else if (!known) {
              const safe = colorKeyWithVariants(live.model, current);
              if (safe !== current) {
                live.setColor(safe);
              }
            } else if (isFreeHexColorKey(current) || live.document.selection.colorHex) {
              live.setColor(current);
            }
          }
          applyPrintOptsFromUrl();
          if (action === 'duplicate') {
            setDesignId('draft');
            setVersionId('draft');
          } else {
            setDesignId(loadedDesignMeta.id);
            setVersionId(loadedDesignMeta.versionId);
          }
          setLoadingDesign(false);
          const remasure = () => {
            if (!cancelled) remasureAllText();
          };
          const fonts = globalThis.document?.fonts;
          if (fonts?.ready) {
            void fonts.ready.then(remasure).catch(remasure);
          } else {
            remasure();
          }
          if (action === 'reorder' && props.commerce !== false) {
            setShowSizes(true);
          }
          return;
        }

        if (!cancelled) {
          setModel(m, props.colorKey);
          applyPrintOptsFromUrl();
          if (loadId && loadId !== 'draft' && action !== 'duplicate') {
            setDesignId(loadId);
            setVersionId(loadId);
          }
          setLoadingDesign(false);
        }
      } catch {
        if (!cancelled) setLoadingDesign(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    props.modelId,
    props.colorKey,
    props.designId,
    props.designAction,
    props.ports.catalog,
    props.ports.identity,
    apiBase,
    setModel,
    replaceDocument,
    remasureAllText,
  ]);

  useEffect(() => {
    const getCart = props.ports.cart.getCart;
    if (!getCart) return;
    void getCart()
      .then((c) => {
        setCartCount(c.cartCount ?? 0);
        if (c.redirectUrl) setCartUrl(c.redirectUrl);
      })
      .catch(() => {});
  }, [props.ports.cart]);

  useEffect(() => {
    if (!isMobile) return;
    if (!selectedId) {
      if (mobileSheet === 'text' || mobileSheet === 'image') setMobileSheet(null);
      return;
    }
    // Don't steal focus from upload / image-edit modal flows
    if (mobileSheet === 'editImage' || mobileSheet === 'upload' || mobileSheet === 'addText') return;
    const obj = document.views
      .find((v) => v.viewId === activeView)
      ?.objects.find((o) => o.id === selectedId);
    if (obj?.type === 'text') setMobileSheet('text');
    else if (obj?.type === 'image') setMobileSheet('image');
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  function openPendingDraft(draft: PendingImageDraft) {
    setPendingImage(draft);
    setReeditObjectId(null);
    if (isMobile) setMobileSheet('editImage');
  }

  async function uploadFile(file: File) {
    const localPreview = URL.createObjectURL(file);
    const area = model?.printAreas[activeView] ?? model?.printAreas.front;
    let widthPx = 800;
    let heightPx = 1000;
    try {
      const probed = await probeImagePixelSize(localPreview);
      if (probed.widthPx > 0 && probed.heightPx > 0) {
        widthPx = probed.widthPx;
        heightPx = probed.heightPx;
      }
    } catch {
      /* keep defaults */
    }

    // Open edit UI immediately with a local preview so iPhone users see the photo
    // while the network upload / derivatives finish.
    setImageUploading(true);
    openPendingDraft({
      assetId: '',
      previewUrl: localPreview,
      ...fitImageToPrintArea(widthPx, heightPx, area?.width ?? 180, area?.height ?? 220),
    });

    try {
      const token = await props.ports.identity.getToken();
      const uploaded = await uploadImageFile(apiBase, file, {
        token: token ?? null,
        widthPx,
        heightPx,
      });
      const minDpi = model?.minDpi ?? 150;
      if (area) {
        const needed = Math.min(
          uploaded.widthPx / (area.width / 25.4),
          uploaded.heightPx / (area.height / 25.4),
        );
        setLowResWarning(needed < minDpi);
      }
      rememberUploadedAsset(apiBase, uploaded.assetId, {
        thumbUrl: uploaded.thumbUrl || uploaded.proxyUrl || localPreview,
        token: token ?? null,
      });
      // Unlock "Usar imagen" as soon as the server has an assetId — don't wait
      // for CDN preload (that was leaving the button stuck on Subiendo…).
      setPendingImage((prev) =>
        prev
          ? {
              ...prev,
              assetId: uploaded.assetId,
              ...fitImageToPrintArea(
                uploaded.widthPx || widthPx,
                uploaded.heightPx || heightPx,
                area?.width ?? 180,
                area?.height ?? 220,
              ),
            }
          : prev,
      );
      setImageUploading(false);

      const meta = await fetchAssetMeta(apiBase, uploaded.assetId, token ?? null);
      const remotePreview =
        uploaded.proxyUrl ||
        uploaded.thumbUrl ||
        meta?.proxyUrl ||
        meta?.thumbUrl ||
        meta?.originalUrl ||
        '';
      const remoteReady = remotePreview ? await preloadImageUrl(remotePreview) : false;
      if (remoteReady) {
        setPendingImage((prev) =>
          prev && prev.assetId === uploaded.assetId
            ? { ...prev, previewUrl: remotePreview }
            : prev,
        );
        window.setTimeout(() => URL.revokeObjectURL(localPreview), 2500);
      }
    } catch (err) {
      setPendingImage(null);
      setImageUploading(false);
      if (isMobile) setMobileSheet('upload');
      window.alert(err instanceof Error ? err.message : 'No se pudo subir la imagen');
      URL.revokeObjectURL(localPreview);
      throw err;
    } finally {
      setImageUploading(false);
    }
  }

  async function pickRecent(assetId: string) {
    const area = model?.printAreas[activeView] ?? model?.printAreas.front;
    const token = await props.ports.identity.getToken();
    const meta = await fetchAssetMeta(apiBase, assetId, token ?? null);
    const previewUrl = meta?.proxyUrl || meta?.thumbUrl || meta?.originalUrl || '';
    if (previewUrl) await preloadImageUrl(previewUrl);
    const wPx = meta?.widthPx && meta.widthPx > 0 ? meta.widthPx : 800;
    const hPx = meta?.heightPx && meta.heightPx > 0 ? meta.heightPx : 1000;
    openPendingDraft({
      assetId,
      previewUrl,
      ...fitImageToPrintArea(wPx, hPx, area?.width ?? 180, area?.height ?? 220),
    });
  }

  function applyImageToCanvas(result: {
    assetId: string;
    widthMm: number;
    heightMm: number;
    mask: ImageMask;
    filter: ImageFilter;
    maskScale: number;
    maskOffsetX: number;
    maskOffsetY: number;
  }) {
    if (!result.assetId) {
      window.alert('La imagen aún se está subiendo. Espera un momento.');
      return;
    }
    const effects =
      result.filter && result.filter !== 'none'
        ? { filter: result.filter, ...(result.filter === 'grayscale' ? { grayscale: true } : {}) }
        : undefined;
    // Mask assets + Edit Image stage are 1:1 — keep placement square or the shape stretches.
    const { widthMm, heightMm } = maskViewportSizeMm(result.widthMm, result.heightMm, result.mask);
    if (reeditObjectId) {
      const st = useEditorStore.getState();
      const prev = st.document.views
        .find((v) => v.viewId === st.activeView)
        ?.objects.find((o) => o.id === reeditObjectId);
      const patch: Record<string, unknown> = {
        assetId: result.assetId,
        width: widthMm,
        height: heightMm,
        mask: result.mask,
        maskScale: result.maskScale,
        maskOffsetX: result.maskOffsetX,
        maskOffsetY: result.maskOffsetY,
        effects,
      };
      if (prev && prev.type === 'image') {
        patch.x = prev.x + (prev.width - widthMm) / 2;
        patch.y = prev.y + (prev.height - heightMm) / 2;
      }
      updateObject(reeditObjectId, patch);
      setReeditObjectId(null);
      setPendingImage(null);
      setPanel('design');
      if (isMobile) setMobileSheet('image');
      return;
    }
    addImage(result.assetId, widthMm, heightMm, {
      mask: result.mask,
      maskScale: result.maskScale,
      maskOffsetX: result.maskOffsetX,
      maskOffsetY: result.maskOffsetY,
      effects,
    });
    setPendingImage(null);
    if (isMobile) setMobileSheet('image');
  }

  /** Place gallery catalog art by reference (no Asset upload). */
  async function placeGalleryOnCanvas(item: { code: string; artworkUrl: string }) {
    const area = model?.printAreas[activeView] ?? model?.printAreas.front;
    const areaW = area?.width ?? 180;
    const areaH = area?.height ?? 220;
    let widthPx = 0;
    let heightPx = 0;
    try {
      const size = await probeImagePixelSize(item.artworkUrl);
      widthPx = size.widthPx;
      heightPx = size.heightPx;
    } catch {
      try {
        const artRes = await fetch(item.artworkUrl, { mode: 'cors', credentials: 'omit' });
        if (artRes.ok) {
          const blob = await artRes.blob();
          const bmp = await createImageBitmap(blob);
          widthPx = bmp.width;
          heightPx = bmp.height;
          bmp.close();
        }
      } catch {
        /* leave unset */
      }
    }
    if (widthPx < 1 || heightPx < 1) {
      // Last resort: landscape placeholder — never invent a tall portrait box
      // (that stretched wide gallery art vertically when CORS blocked sizing).
      widthPx = 1000;
      heightPx = 500;
    }
    // Fill print area on the limiting axis; keep native aspect (wide art → full width, short height).
    const { widthMm, heightMm } = fitImageToPrintArea(widthPx, heightPx, areaW, areaH);
    setLowResWarning(false);
    addGalleryImage(item.code, widthMm, heightMm);
  }

  // Preload gallery artwork from ficha (?ox_gallery=) straight onto the canvas.
  useEffect(() => {
    const code = props.galleryCode?.trim();
    if (!code || !model || galleryBootstrapped.current) return;
    if (initialDesignId !== 'draft') return;
    galleryBootstrapped.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const store = (props.storeId || 'ox').trim() || 'ox';
        const base = apiBase.replace(/\/$/, '');
        const metaRes = await fetch(
          `${base}/gallery/items/${encodeURIComponent(code)}?storeId=${encodeURIComponent(store)}`,
        );
        if (!metaRes.ok || cancelled) return;
        const meta = (await metaRes.json()) as { artworkUrl?: string; code?: string };
        if (!meta.artworkUrl || cancelled) return;
        await placeGalleryOnCanvas({
          code: meta.code || code,
          artworkUrl: meta.artworkUrl,
        });
      } catch {
        /* non-fatal — user can still pick from Galería */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, props.galleryCode, props.storeId, apiBase, initialDesignId]);

  function openReedit() {
    const obj = document.views
      .find((v) => v.viewId === activeView)
      ?.objects.find((o) => o.id === selectedId);
    if (!obj || obj.type !== 'image') return;
    if (obj.galleryCode) return; // gallery refs: no mask/filter edit
    if (!obj.assetId) return;
    setReeditObjectId(obj.id);
    const cachedThumb =
      loadRecentAssets().find((r) => r.assetId === obj.assetId)?.thumbUrl || '';
    // Open sheet immediately so iPhone doesn't sit on a blank wait; fill URL as meta arrives.
    setPendingImage({
      assetId: obj.assetId,
      previewUrl: cachedThumb,
      widthMm: obj.width,
      heightMm: obj.height,
      mask: obj.mask,
      filter: obj.effects?.filter,
      grayscale: !!obj.effects?.grayscale,
      maskScale: obj.maskScale ?? 1,
      maskOffsetX: obj.maskOffsetX ?? 0,
      maskOffsetY: obj.maskOffsetY ?? 0,
    });
    if (isMobile) setMobileSheet('editImage');

    void (async () => {
      const token = await props.ports.identity.getToken();
      const meta = await fetchAssetMeta(apiBase, obj.assetId!, token ?? null);
      const area = model?.printAreas[activeView] ?? model?.printAreas.front;
      const wPx = meta?.widthPx && meta.widthPx > 0 ? meta.widthPx : 0;
      const hPx = meta?.heightPx && meta.heightPx > 0 ? meta.heightPx : 0;
      const size =
        (obj.mask ?? 'none') === 'none' && wPx > 0 && hPx > 0
          ? fitImageToPrintArea(wPx, hPx, area?.width ?? 180, area?.height ?? 220)
          : { widthMm: obj.width, heightMm: obj.height };
      const previewUrl =
        meta?.proxyUrl || meta?.thumbUrl || meta?.originalUrl || cachedThumb || '';
      const remoteReady = previewUrl && previewUrl !== cachedThumb
        ? await preloadImageUrl(previewUrl)
        : !!previewUrl;
      const nextPreview = remoteReady
        ? previewUrl
        : cachedThumb || previewUrl || '';
      setPendingImage((prev) =>
        prev && prev.assetId === obj.assetId
          ? {
              ...prev,
              previewUrl: nextPreview || prev.previewUrl,
              widthMm: size.widthMm,
              heightMm: size.heightMm,
            }
          : prev,
      );
    })();
  }

  async function save(): Promise<{ designId: string; versionId: string; objectCount: number } | null> {
    setSaving(true);
    setSaveNotice(null);
    try {
      // Always read live store — save() may be called via a stale registerSaveApi closure
      // (web component does not re-render on every canvas edit).
      const live = useEditorStore.getState();
      const doc = live.document;
      const liveModel = live.model;
      const currentDesignId = designIdRef.current;
      const token = await props.ports.identity.getToken();
      const templateId = doc.templateId || liveModel?.templateId || 'polo-clasico-unisex';
      const res = await fetch(`${apiBase}/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...oxAuthHeaders(token),
        },
        body: JSON.stringify({
          id: currentDesignId === 'draft' ? undefined : currentDesignId,
          templateId,
          document: { ...doc, templateId },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.id) {
        setSaveNotice(
          typeof data?.error === 'string' ? `Error: ${data.error}` : 'No se pudo guardar el diseño.',
        );
        window.setTimeout(() => setSaveNotice(null), 5000);
        return null;
      }
      const id = data.id as string;
      const vid = (data.versionId as string) || id;
      const previewUrl =
        (typeof data.previewUrl === 'string' && data.previewUrl) ||
        `${apiBase.replace(/\/$/, '')}/designs/${encodeURIComponent(id)}/preview?view=front&w=280`;
      setDesignId(id);
      designIdRef.current = id;
      setVersionId(vid);
      props.onSave?.(id, vid);
      // Additive event for hosts (Ox Studio wizard). Bubbles through shadow/light DOM.
      try {
        window.dispatchEvent(
          new CustomEvent('ox:design-saved', {
            bubbles: true,
            composed: true,
            detail: {
              designId: id,
              versionId: vid,
              templateId,
              previewUrl,
            },
          }),
        );
      } catch {
        /* ignore */
      }
      await warmDesignPreviews(id, { width: 280, waitForImages: false, fresh: true });

      // From cart “Editar diseño”: refresh line preview/version without re-picking sizes.
      if (fromCart && props.ports.cart.syncDesign) {
        try {
          const synced = await props.ports.cart.syncDesign({
            designId: id,
            versionId: vid,
            replaceCartKey: cartKeyFromUrl,
            viewMetrics: getUsedViewMetrics(
              useEditorStore.getState().document,
              useEditorStore.getState().model?.printAreas,
            ),
            ...printServiceQuoteFields(useEditorStore.getState().document.selection),
            ...modelQuoteFields(useEditorStore.getState().model),
          });
          if (synced.cartCount != null) setCartCount(synced.cartCount);
          if (synced.redirectUrl) setCartUrl(synced.redirectUrl);
          setSaveNotice('Diseño actualizado en la cesta. Puedes volver al carrito.');
        } catch {
          setSaveNotice('Diseño guardado. Pulsa «Actualizar cesta» para aplicar cambios.');
        }
      } else if (currentDesignId !== 'draft') {
        setSaveNotice('Diseño actualizado.');
      } else {
        setSaveNotice('Diseño guardado.');
      }
      window.setTimeout(() => setSaveNotice(null), 5000);
      const objectCount = doc.views.reduce((n, v) => n + (v.objects?.length ?? 0), 0);
      return { designId: id, versionId: vid, objectCount };
    } catch {
      setSaveNotice('No se pudo guardar el diseño.');
      window.setTimeout(() => setSaveNotice(null), 5000);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function openSaveTemplate() {
    setSaveTplError('');
    const ownerRef = await props.ports.identity.getOwnerRef();
    if (!ownerRef || ownerRef.startsWith('guest:')) {
      const login = props.ports.studio?.loginUrl;
      if (login) {
        window.location.href = login;
        return;
      }
      setSaveTplError('Inicia sesión para guardar una plantilla.');
      setSaveTplOpen(true);
      return;
    }
    setSaveTplOpen(true);
    setTplCollectionsLoading(true);
    try {
      const list = await props.ports.studio?.listCollections?.();
      setTplCollections(list?.items ?? []);
    } catch {
      setTplCollections([]);
    } finally {
      setTplCollectionsLoading(false);
    }
  }

  async function createTplCollection(name: string) {
    const create = props.ports.studio?.createCollection;
    if (!create) throw new Error('No se pueden crear colecciones aquí.');
    const created = await create(name);
    setTplCollections((prev) => {
      if (prev.some((c) => c.id === created.id)) return prev;
      return [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
    });
    return created;
  }

  async function confirmSaveTemplate(input: { title: string; collectionIds: string[] }) {
    const studio = props.ports.studio;
    if (!studio?.createFromDesign) return;
    setSaveTplBusy(true);
    setSaveTplError('');
    try {
      const saved = await save();
      if (!saved?.designId || (saved.objectCount ?? 0) < 1) {
        throw new Error('Añade texto o una imagen antes de guardar la plantilla.');
      }
      const liveModel = useEditorStore.getState().model;
      if (!liveModel) {
        throw new Error('No hay producto cargado.');
      }
      const variants = await buildTemplateVariantsFromModel(
        liveModel,
        props.ports.pricing,
        saved.designId,
      );
      const created = await studio.createFromDesign({
        title: input.title,
        catalogProductRef: liveModel.id,
        designRef: saved.designId,
        designVersionRef: saved.versionId,
        collectionIds: input.collectionIds,
        marginPercent: 50,
        status: 'ready',
        variants: variants.map((v, i) => ({ ...v, sortOrder: i, active: true })),
      });
      setSaveTplOpen(false);
      // Stay in the designer (Gelato-style): toast only, no redirect to Studio review.
      let templatesHref = studio.templatesUrl || '';
      if (templatesHref && created?.id) {
        try {
          const url = new URL(templatesHref, window.location.origin);
          url.searchParams.delete('oxs_template');
          url.searchParams.set('oxs_view', 'list');
          templatesHref = url.toString();
        } catch {
          /* keep as-is */
        }
      }
      setTplToast({ title: input.title, url: templatesHref || undefined });
      window.setTimeout(() => setTplToast(null), 6000);
    } catch (e) {
      setSaveTplError(e instanceof Error ? e.message : 'No se pudo guardar la plantilla');
    } finally {
      setSaveTplBusy(false);
    }
  }

  useEffect(() => {
    if (!props.registerSaveApi) return;
    props.registerSaveApi({
      // Call through getState-backed save; avoid capturing a stale document closure.
      save: () => save(),
      assertReady: () => {
        const live = useEditorStore.getState();
        if (live.model?.printService && !live.document.selection.garmentPrintType) {
          throw new Error(
            'Elige el tipo de impresión (sin o con base blanca) antes de continuar.',
          );
        }
      },
    });
    return () => props.registerSaveApi?.(null);
    // Re-register when the callback identity changes (web component render).
    // save itself always reads live store + designIdRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.registerSaveApi]);

  /** Save + warm composited previews (same images used in cart / Ver modal). */
  async function warmDesignPreviews(
    id?: string,
    opts?: { width?: number; waitForImages?: boolean; fresh?: boolean },
  ) {
    const width = opts?.width ?? 280;
    const waitForImages = opts?.waitForImages ?? true;
    const fresh = opts?.fresh ?? false;
    const base = apiBase.replace(/\/$/, '');
    let did = id;
    if (!did || did === 'draft') {
      const saved = await save();
      did = saved?.designId;
    }
    if (!did) return;
    const views = (model?.views.map((v) => v.id) ?? ['front', 'back']) as ViewId[];
    const next: Partial<Record<ViewId, string>> = {};
    const stamp = Date.now();
    for (const view of views) {
      const freshQ = fresh ? '&fresh=1' : '';
      next[view] = `${base}/designs/${encodeURIComponent(did)}/preview?view=${view}&w=${width}&t=${stamp}${freshQ}`;
    }
    if (waitForImages) {
      // Wait until the server has rendered each preview before publishing URLs,
      // so the Ver UI can keep showing a loading state.
      await Promise.all(
        Object.values(next).map(
          (url) =>
            new Promise<void>((resolve) => {
              if (!url) return resolve();
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              img.src = url;
            }),
        ),
      );
      setDesignPreviewUrls((prev) => ({ ...prev, ...next }));
      return;
    }
    setDesignPreviewUrls((prev) => ({ ...prev, ...next }));
    for (const url of Object.values(next)) {
      if (!url) continue;
      const img = new Image();
      img.src = url;
    }
  }

  async function openPreview() {
    setShowPreview(true);
    setPreviewLoading(true);
    setDesignPreviewUrls({}); // drop stale composites while regenerating
    try {
      // Always save current canvas first — otherwise Ver shows the last persisted version.
      const saved = await save();
      if (saved?.designId) {
        await warmDesignPreviews(saved.designId, { width: 600, waitForImages: true, fresh: true });
      }
    } finally {
      setPreviewLoading(false);
    }
  }

  /**
   * After a normal “Añadir a la cesta”, copy the current document into a NEW
   * design id. The cart keeps the design that was just added; further canvas
   * edits / saves must not mutate that carted snapshot.
   * Skipped when editing from the cart (`ox_cart_key`).
   */
  async function forkDesignAfterCartAdd(): Promise<void> {
    if (fromCart) return;
    const live = useEditorStore.getState();
    const doc = live.document;
    const liveModel = live.model;
    const templateId = doc.templateId || liveModel?.templateId || 'polo-clasico-unisex';
    try {
      const token = await props.ports.identity.getToken();
      const res = await fetch(`${apiBase}/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...oxAuthHeaders(token),
        },
        body: JSON.stringify({
          // Omit id → always create a fresh design row.
          templateId,
          document: { ...doc, templateId },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.id) return;
      const id = data.id as string;
      const vid = (data.versionId as string) || id;
      setDesignId(id);
      designIdRef.current = id;
      setVersionId(vid);
      // Drop cart-edit / stale design query params so a refresh loads the fork.
      try {
        const url = new URL(window.location.href);
        let dirty = false;
        if (url.searchParams.has('ox_cart_key')) {
          url.searchParams.delete('ox_cart_key');
          dirty = true;
        }
        if (url.searchParams.has('ox_design') || url.searchParams.has('ox_image')) {
          url.searchParams.set('ox_design', id);
          url.searchParams.set('ox_image', id);
          dirty = true;
        }
        if (dirty) {
          window.history.replaceState({}, '', url.toString());
        }
      } catch {
        /* ignore */
      }
    } catch {
      /* non-fatal — next save may still overwrite the carted design */
    }
  }

  /** Size modal does not need previews — open ASAP after save; warm cart thumbs in background. */
  const openBuy = () => {
    if (!commerce) return;
    void save().then((saved) => {
      if (!saved?.designId) return;
      setShowSizes(true);
      void warmDesignPreviews(saved.designId, { width: 280, waitForImages: false });
    });
  };

  const tabActive: 'text' | 'upload' | 'gallery' | 'products' | null =
    mobileSheet === 'addText' || mobileSheet === 'text'
      ? 'text'
      : mobileSheet === 'upload' || mobileSheet === 'editImage' || mobileSheet === 'image'
        ? 'upload'
        : mobileSheet === 'gallery'
          ? 'gallery'
          : productsMode ||
              mobileSheet === 'productDetails' ||
              mobileSheet === 'changeProduct'
            ? 'products'
            : null;

  const previewBase = apiBase.replace(/\/$/, '');
  const modelViews = model?.views ?? [];
  const addedPreviews = modelViews
    .map((v) => {
      const url =
        designPreviewUrls[v.id] ??
        (designId !== 'draft'
          ? `${previewBase}/designs/${encodeURIComponent(designId)}/preview?view=${v.id}&w=600&v=${encodeURIComponent(versionId)}`
          : '');
      return url ? { viewId: v.id, label: v.label, url } : null;
    })
    .filter((p): p is { viewId: typeof modelViews[number]['id']; label: string; url: string } => !!p);

  const previewModal = showPreview && (
    <PreviewModal
      urls={designPreviewUrls}
      loading={previewLoading}
      onClose={() => setShowPreview(false)}
    />
  );

  const sizeModal = commerce && showSizes && (
    <SizeQtyModal
      sheetMode={isMobile}
      pricing={props.ports.pricing}
      cart={props.ports.cart}
      catalog={props.ports.catalog}
      designId={designId}
      versionId={versionId}
      onClose={() => setShowSizes(false)}
      onAdded={(result) => {
        setCartCount(result.cartCount);
        if (result.redirectUrl) setCartUrl(result.redirectUrl);
        setAdded({
          articleCount: result.articleCount,
          redirectUrl: result.redirectUrl,
        });
        // Detach canvas from the design now sitting in the cart.
        void forkDesignAfterCartAdd();
      }}
    />
  );

  const addedModal = commerce && added && addedPreviews.length > 0 && (
    <AddedToCartModal
      articleCount={added.articleCount}
      previews={addedPreviews}
      cartUrl={added.redirectUrl}
      onContinue={() => setAdded(null)}
    />
  );

  const saveTplModal = saveTplOpen && (
    <SaveAsTemplateModal
      defaultName={model?.name || 'Mi plantilla'}
      busy={saveTplBusy}
      error={saveTplError}
      collections={tplCollections}
      collectionsLoading={tplCollectionsLoading}
      onCreateCollection={createTplCollection}
      onClose={() => {
        if (!saveTplBusy) {
          setSaveTplOpen(false);
          setSaveTplError('');
        }
      }}
      onSave={(input) => void confirmSaveTemplate(input)}
    />
  );

  const tplToastEl = tplToast && (
    <div className="ox-tpl-toast" role="status">
      <span className="ox-tpl-toast__ok" aria-hidden>
        ✓
      </span>
      <div className="ox-tpl-toast__body">
        <strong>Plantilla guardada</strong>
        <span>
          «{tplToast.title}» está en la sección de plantillas.
          {tplToast.url ? (
            <>
              {' '}
              <a href={tplToast.url}>Ver plantillas</a>
            </>
          ) : null}
        </span>
      </div>
      <button
        type="button"
        className="ox-tpl-toast__x"
        aria-label="Cerrar"
        onClick={() => setTplToast(null)}
      >
        ×
      </button>
    </div>
  );

  const themeStyle = uiThemeStyle(props.uiTheme);

  if (isMobile) {
    return (
      <div className="ox-root ox-mobile" style={themeStyle}>
        <CanvasPrivacyBanner />
        <MobileHeader
          cartCount={cartCount}
          cartUrl={cartUrl}
          logoUrl={props.logoUrl}
          logoHref={props.logoHref}
          showCart={commerce}
          showSaveTemplate={canSaveTemplate}
          onSaveTemplate={() => void openSaveTemplate()}
          priceLabel={commerce ? priceLabel : undefined}
          onPriceClick={
            commerce && liveQuote.quote?.total ? () => setLiveBreakdownOpen(true) : undefined
          }
          onDetails={() => {
            setProductsMode(true);
            setMobileSheet('productDetails');
          }}
        />
        <div className="ox-m-stage">
          <div className="ox-canvas-area">
            <DesignerCanvas />
            <MobileCanvasOverlays
              saving={saving}
              saveLabel={mobileSaveLabel}
              onSave={() => void save()}
              onPreview={() => {
                setPreviewLoading(true);
                setDesignPreviewUrls({});
                setMobileSheet('preview');
                void (async () => {
                  try {
                    const saved = await save();
                    if (saved?.designId) {
                      await warmDesignPreviews(saved.designId, {
                        width: 600,
                        waitForImages: true,
                        fresh: true,
                      });
                    }
                  } finally {
                    setPreviewLoading(false);
                  }
                })();
              }}
            />
          </div>
          {productsMode && (
            <MobileProductActions
              onDetails={() => setMobileSheet('productDetails')}
              onChangeProduct={() => setMobileSheet('changeProduct')}
            />
          )}
          <MobileViewSwitcher />
        </div>
        <MobileTabBar
          active={tabActive}
          showBuy={commerce}
          onText={() => {
            setProductsMode(false);
            setMobileSheet('addText');
          }}
          onUpload={() => {
            setProductsMode(false);
            setMobileSheet('upload');
          }}
          onGallery={() => {
            setProductsMode(false);
            setMobileSheet('gallery');
          }}
          onProducts={() => {
            setProductsMode(true);
            setMobileSheet(null);
          }}
          onBuy={openBuy}
        />

        {mobileSheet === 'addText' && (
          <TextAddSheet
            onClose={() => setMobileSheet(null)}
            onAdded={() => setMobileSheet('text')}
          />
        )}
        {mobileSheet === 'text' && (
          <TextEditSheet
            onClose={() => {
              setMobileSheet(null);
              select(null);
            }}
          />
        )}
        {mobileSheet === 'image' && (
          <ImageEditSheet
            onClose={() => {
              setMobileSheet(null);
              select(null);
            }}
            onReedit={openReedit}
          />
        )}
        {mobileSheet === 'garmentColor' && (
          <GarmentColorSheet onClose={() => setMobileSheet(null)} />
        )}
        {mobileSheet === 'upload' && (
          <UploadSheet
            apiBase={apiBase}
            onClose={() => setMobileSheet(null)}
            onUpload={uploadFile}
            onPickRecent={(id) => void pickRecent(id)}
            getToken={() => props.ports.identity.getToken()}
          />
        )}
        {mobileSheet === 'gallery' && (
          <GallerySheet
            apiBase={apiBase}
            storeId={props.storeId ?? 'ox'}
            onClose={() => setMobileSheet(null)}
            onPickArtwork={async (item) => {
              await placeGalleryOnCanvas({
                code: item.code,
                artworkUrl: item.artworkUrl,
              });
            }}
          />
        )}
        {mobileSheet === 'editImage' && pendingImage && (
          <EditImageSheet
            apiBase={apiBase}
            draft={pendingImage}
            uploading={imageUploading || !pendingImage.assetId}
            getToken={() => props.ports.identity.getToken()}
            onClose={() => {
              const backToImage = !!(reeditObjectId || selectedId);
              setPendingImage(null);
              setReeditObjectId(null);
              setImageUploading(false);
              setMobileSheet(backToImage ? 'image' : null);
            }}
            onUse={(result) => applyImageToCanvas(result)}
          />
        )}
        {mobileSheet === 'productDetails' && (
          <ProductDetailsSheet onClose={() => setMobileSheet(null)} />
        )}
        {mobileSheet === 'changeProduct' && (
          <ChangeProductSheet catalog={props.ports.catalog} onClose={() => setMobileSheet(null)} />
        )}
        {mobileSheet === 'preview' && (
          <PreviewSheet
            urls={designPreviewUrls}
            loading={previewLoading}
            onClose={() => setMobileSheet(null)}
          />
        )}
        {sizeModal}
        {addedModal}
        {saveTplModal}
        {tplToastEl}
        {previewModal}
        {liveBreakdownOpen && liveQuote.quote && model && (
          <PriceBreakdownSheet
            quote={liveQuote.quote}
            productName={model.name}
            colorLabel={liveColorLabel}
            articleCount={1}
            onClose={() => setLiveBreakdownOpen(false)}
            sheetMode
          />
        )}
      </div>
    );
  }

  const editingPanel =
    panel === 'addText' ||
    panel === 'text' ||
    panel === 'image' ||
    panel === 'design' ||
    panel === 'gallery';
  const showHome = !selectedId && !editingPanel && !changeProductOpen;
  const showChrome = !!selectedId || editingPanel;
  const showSide = showHome || editingPanel;
  const mainClass = [
    'ox-main',
    showHome ? 'ox-main--home' : '',
    showChrome && !showSide ? 'ox-main--no-side' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="ox-root" style={themeStyle}>
      <CanvasPrivacyBanner />
      {loadingDesign && (
        <div className="ox-design-loading" aria-live="polite">
          Cargando diseño…
        </div>
      )}
      <DesktopTopBar
        cartCount={cartCount}
        cartUrl={cartUrl}
        logoUrl={props.logoUrl}
        logoHref={props.logoHref}
        priceLabel={priceLabel}
        quote={liveQuote.quote}
        showCart={commerce}
        showSaveTemplate={canSaveTemplate}
        onSaveTemplate={() => void openSaveTemplate()}
      />
      <div className={mainClass}>
        {showChrome && <LeftToolbar />}
        {showHome && (
          <HomePanel
            catalog={props.ports.catalog}
            onUploadClick={() => {
              useEditorStore.getState().setTool('upload');
              setPanel('image');
            }}
          />
        )}
        {panel === 'addText' && <AddTextPanel />}
        {panel === 'text' && <TextPanel key={selectedId ?? 'text'} />}
        {(panel === 'image' || panel === 'design') && (
          <ImagePanel
            apiBase={apiBase}
            onUpload={uploadFile}
            onPickRecent={(id) => void pickRecent(id)}
            onReedit={openReedit}
            getToken={() => props.ports.identity.getToken()}
          />
        )}
        {panel === 'gallery' && (
          <GalleryPanel
            apiBase={apiBase}
            storeId="ox"
            onPickArtwork={async (item) => {
              await placeGalleryOnCanvas({
                code: item.code,
                artworkUrl: item.artworkUrl,
              });
            }}
          />
        )}
        <div className="ox-canvas-area">
          <DesignerCanvas />
        </div>
        <ViewSwitcher
          previewOpen={showPreview}
          onTogglePreview={() => {
            if (showPreview) setShowPreview(false);
            else void openPreview();
          }}
        />
      </div>
      <FooterBar
        saving={saving}
        saveLabel={saveLabel}
        chooseSizeLabel={chooseSizeLabel}
        notice={saveNotice}
        priceLabel={priceLabel}
        quote={liveQuote.quote}
        showCommerce={commerce}
        onSave={() => void save()}
        onChooseSize={openBuy}
        onOpenProduct={() => setChangeProductOpen(true)}
      />
      {liveBreakdownOpen && liveQuote.quote && model && (
        <PriceBreakdownSheet
          quote={liveQuote.quote}
          productName={model.name}
          colorLabel={liveColorLabel}
          articleCount={1}
          onClose={() => setLiveBreakdownOpen(false)}
        />
      )}
      {(changeProductOpen || panel === 'products') && (
        <ChangeProductModal
          catalog={props.ports.catalog}
          onClose={() => {
            setChangeProductOpen(false);
            if (panel === 'products') setPanel('none');
          }}
        />
      )}
      {!isMobile && pendingImage && (
        <EditImageModal
          apiBase={apiBase}
          draft={pendingImage}
          uploading={imageUploading || !pendingImage.assetId}
          getToken={() => props.ports.identity.getToken()}
          onClose={() => {
            setPendingImage(null);
            setReeditObjectId(null);
            setImageUploading(false);
          }}
          onUse={(result) => applyImageToCanvas(result)}
        />
      )}
      {sizeModal}
      {addedModal}
      {saveTplModal}
      {tplToastEl}
      {previewModal}
    </div>
  );
}
